import { Component, DestroyRef, inject, OnInit, Renderer2, RendererFactory2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { delay, filter, map, switchMap, tap } from 'rxjs/operators';

import { ColorModeService } from '@coreui/angular';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset } from './icons/icon-subset';

import { TranslatePipe, TranslateDirective, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/services/api.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { has, set } from 'lodash-es';
import { DOCUMENT } from "@angular/common";
import { EInvoicingService } from 'src/app/services/e-invoicing.service';
import { StripeService } from 'src/app/services/stripe.service';

declare var Tawk_API: any;

@Component({
    selector: 'app-root',
    template: '<router-outlet />',
    standalone: true,
    imports: [RouterOutlet, TranslatePipe, TranslateDirective]
})

export class AppComponent implements OnInit {
    title = 'ProcureX • BuildSpace';
    routeTitle = '';

    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
    readonly #router = inject(Router);
    readonly #titleService = inject(Title);

    readonly #colorModeService = inject(ColorModeService);
    readonly #iconSetService = inject(IconSetService);

    private renderer: Renderer2 = inject(Renderer2);
    private _document: Document = inject(DOCUMENT);

    constructor(private rendererFactory: RendererFactory2, public translate: TranslateService, private router: Router, private api: ApiService, private auth: AuthenticationService, private eInvoicingService: EInvoicingService, private stripeService: StripeService) {
        this.#titleService.setTitle(this.title);
        // iconSet singleton
        this.#iconSetService.icons = { ...iconSubset };
        this.#colorModeService.localStorageItemName.set('coreui-free-angular-admin-template-theme-default');
        this.#colorModeService.eventName.set('ColorSchemeChange');

        translate.addLangs(['en', 'ms']);

        // set default language to use
        translate.setDefaultLang('en');

        let language: any = localStorage.getItem('language') ?? 'en';
        translate.use(language);

        this.renderer = rendererFactory.createRenderer(null, null);
    }

    ngOnInit(): void {
        this.getRouteTitle();

        const s = this.renderer.createElement('script');

        s.type = 'text/javascript';
        s.text = 'var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date(); ' +
            '(function(){ ' +
            'var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0]; ' +
            's1.async=true; ' +
            's1.src=\'https://embed.tawk.to/67e0ffaae7363d190f550d47/1in42esvr\'; ' +
            's1.charset=\'UTF-8\'; ' +
            's1.setAttribute(\'crossorigin\',\'*\'); ' +
            's0.parentNode.insertBefore(s1,s0); ' +
            '})();';

        // Log user activity on every route change
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                let currentRoute = this.#activatedRoute.snapshot;
                while (currentRoute.firstChild) {
                    currentRoute = currentRoute.firstChild;
                }

                // Check if the route has AuthGuard
                if (currentRoute.routeConfig?.canActivate) {

                    // Load Tawk widget script 
                    this.renderer.appendChild(this._document.body, s);
                    Tawk_API.onLoad = this.loadedEvent.bind(this);

                    setTimeout(() => {
                        if (this.routeTitle) {
                            //Create log for navigation changed
                            this.api.createLog(this.routeTitle, event.urlAfterRedirects).subscribe((res: any) => { });
                        }
                    }, 1000);
                }
            }
            window.scrollTo(0, 0);
        });

        this.#router.events.pipe(
            takeUntilDestroyed(this.#destroyRef)
        ).subscribe((evt) => {
            if (!(evt instanceof NavigationEnd)) {
                return;
            }
        });

        this.#activatedRoute.queryParams
            .pipe(
                delay(1),
                map(params => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
                filter(theme => ['dark', 'light', 'auto'].includes(theme)),
                tap(theme => {
                    this.#colorModeService.colorMode.set(theme);
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();

        // this.eInvoicingService.intermediaryLogin("C24589842050").subscribe((res : any) => {
        //   console.log("E-Invoicing Login Success", res);
        //   this.eInvoicingService.setToken(res.access_token);

        //   this.eInvoicingService.searchTaxpayerTIN('BRN', '201101045004').subscribe((res : any) => {
        //     console.log("E-Invoicing Validate TIN Success", res);
        //   });

        // const invoice = {};

        // this.eInvoicingService.submitDocument(invoice, "C21998774020", "201101045004").subscribe((res : any) => {
        //   console.log("E-Invoicing Submit success", res);
        // }, (err : any) => {
        //   console.log("E-Invoicing Error", err.error);
        // });

        // this.eInvoicingService.getDocument("8SK3GCB0A3W7F3NNDVZZ0KYJ10").subscribe((res : any) => {
        //   console.log("E-Invoicing Get Document success", res);
        // });

        // this.eInvoicingService.cancelDocument("X2XYQ0HVN0Z00W95YJWE1KYJ10").subscribe((res : any) => {
        //   console.log("E-Invoicing Cancel success", res);
        // });

        // this.eInvoicingService.rejectDocument("XTYZVWXNWCP6XP9HKCY8ZBVJ10").subscribe((res : any) => {
        //   console.log("E-Invoicing Reject success", res);
        // });
        // });

        // this.stripeService.getStripeProducts().subscribe((products: any) => {
        //   console.log("Stripe Products", products);
        // });

    }

    private loadedEvent() {
        console.log('Tawk API Loaded');

        // Secure Mode - Set User Attributes
        Tawk_API.setAttributes({
            name: this.auth.user().name,
            email: this.auth.user().email,
            hash: this.auth.user().tawkto_hash
        }, function (error: any) {
            if (error) {
                console.log('Tawk API Set Attributes Error', error);
            } else {
                console.log('Tawk API Set Attributes Success');
                // minimize the chat widget
                Tawk_API.minimize();
            }
        });

        Tawk_API.visitor = {
            name: this.auth.user().name,
            email: this.auth.user().email,
            hash: this.auth.user().tawkto_hash
        };

    }

    private getRouteTitle(): void {
        this.#router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            map(() => {
                let route = this.#activatedRoute;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return route.snapshot.data['title'];
            }),
            tap(title => this.routeTitle = title),
            filter(title => !!title),
            switchMap(title => this.translate.stream(title)),
            takeUntilDestroyed(this.#destroyRef)
        ).subscribe(text => this.#titleService.setTitle(text));
    }
}
